// ============================================================================
// Enriched Tranches Cards Component
// Card-based layout for detailed per-tranche metrics with expandable sections
// ============================================================================

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from './ui/card';
import type { EnrichedBuyTranche, ExitExecution } from '../types';
import { formatCurrency, formatCurrencyWithSign, formatResidualRisk, getPnLColorClass } from '../utils/formatters';

interface EnrichedTranchesCardsProps {
  tranches: EnrichedBuyTranche[];
  isLoading?: boolean;
  showHeader?: boolean;  // Whether to show the "Investment Tranches" header
}

/**
 * Single Tranche Card Component
 */
const TrancheCard: React.FC<{ 
  tranche: EnrichedBuyTranche; 
  index: number;
}> = ({ tranche, index }) => {
  const [isExpanded, setIsExpanded] = useState(index === 0); // First card open by default
  
  const hasExits = tranche.exitExecutions && tranche.exitExecutions.length > 0;
  const percentOpen = tranche.quantityBought > 0 
    ? Math.round((tranche.quantityOpen / tranche.quantityBought) * 100) 
    : 0;
  
  return (
    <Card className="mb-4 overflow-hidden border border-gray-200 shadow-sm">
      {/* Card Header - Always Visible */}
      <div 
        className="flex items-center cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Blue Left Border Accent */}
        <div className="w-1 self-stretch bg-blue-500" />
        
        {/* Main Content */}
        <div className="flex-1 p-4">
          {/* Header Row */}
          <div className="grid grid-cols-8 gap-4 items-start">
            {/* Tranche ID & Date */}
            <div>
              <div className="font-semibold text-gray-900">T{index + 1}</div>
              <div className="text-sm text-gray-500">{tranche.date}</div>
            </div>
            
            {/* Quantity */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Quantity</div>
              <div className="font-medium text-gray-900">
                {tranche.quantityBought} ({tranche.quantityOpen} open)
              </div>
            </div>
            
            {/* Avg Buy Price */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Avg Buy Price</div>
              <div className="font-medium text-gray-900">{formatCurrency(tranche.avgBuyPrice)}</div>
            </div>
            
            {/* Invested */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Invested</div>
              <div className="font-medium text-gray-900">{formatCurrency(tranche.invested)}</div>
            </div>
            
            {/* Realized P&L */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Realized P&L</div>
              <div className={`font-medium ${getPnLColorClass(tranche.realizedPnL)}`}>
                {tranche.realizedPnL !== 0 ? formatCurrencyWithSign(tranche.realizedPnL) : '₹0'}
              </div>
            </div>
            
            {/* Unrealized P&L */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Unrealized P&L</div>
              <div className={`font-medium ${tranche.unrealizedPnL !== null ? getPnLColorClass(tranche.unrealizedPnL) : 'text-gray-500'}`}>
                {tranche.unrealizedPnL !== null ? formatCurrencyWithSign(tranche.unrealizedPnL) : 'N/A'}
              </div>
            </div>
            
            {/* Residual Risk */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Residual Risk</div>
              <div className={`font-medium ${formatResidualRisk(tranche.residualRisk).colorClass}`}>
                {formatResidualRisk(tranche.residualRisk).value}
              </div>
            </div>
            
            {/* Original SL */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Original SL</div>
              <div className="font-medium text-gray-900">
                {tranche.stoploss ? formatCurrency(tranche.stoploss) : 'N/A'}
              </div>
            </div>
          </div>
        </div>
        
        {/* Expand/Collapse Button */}
        <div className="pr-4">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Expanded Section */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          {/* Exit Executions Section */}
          <div className="p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Exit Executions for This Tranche</h4>
            
            {hasExits ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Exit Date</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Qty Sold</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Sell Price</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Exit Value</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Realized P&L</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">R-Multiple</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Exit Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tranche.exitExecutions.map((exit, idx) => (
                      <ExitRow key={`${tranche.trancheId}-exit-${idx}`} exit={exit} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500 text-sm border border-dashed border-gray-200 rounded-lg">
                No exits recorded yet
              </div>
            )}
          </div>
          
          {/* Footer Summary - Light Blue Background */}
          <div className="bg-blue-50 px-4 py-3">
            <div className="grid grid-cols-4 gap-8">
              {/* Remaining Quantity */}
              <div>
                <div className="text-xs text-gray-500 mb-1">Remaining Quantity</div>
                <div className="font-semibold text-blue-700">{tranche.quantityOpen}</div>
              </div>
              
              {/* Remaining Unrealized P&L */}
              <div>
                <div className="text-xs text-gray-500 mb-1">Remaining Unrealized P&L</div>
                <div className={`font-semibold ${getPnLColorClass(tranche.unrealizedPnL)}`}>
                  {formatCurrencyWithSign(tranche.unrealizedPnL)}
                </div>
              </div>
              
              {/* Active Risk at Current SL */}
              <div>
                <div className="text-xs text-gray-500 mb-1">Active Risk at Current SL</div>
                <div className={`font-semibold ${formatResidualRisk(tranche.residualRisk).colorClass}`}>
                  {formatResidualRisk(tranche.residualRisk).value}
                </div>
              </div>
              
              {/* % of Original Still Open */}
              <div>
                <div className="text-xs text-gray-500 mb-1">% of Original Still Open</div>
                <div className="font-semibold text-gray-900">{percentOpen}%</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

/**
 * Exit Execution Row Component
 */
const ExitRow: React.FC<{ exit: ExitExecution }> = ({ exit }) => {
  const exitDate = new Date(exit.exitDate).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  
  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <td className="py-2 px-3 text-sm text-gray-900">{exitDate}</td>
      <td className="py-2 px-3 text-sm text-gray-900">{exit.qtySold}</td>
      <td className="py-2 px-3 text-sm text-gray-900">{formatCurrency(exit.sellPrice)}</td>
      <td className="py-2 px-3 text-sm text-gray-900">{formatCurrency(exit.exitValue)}</td>
      <td className={`py-2 px-3 text-sm font-medium ${getPnLColorClass(exit.realizedPnL)}`}>
        {formatCurrencyWithSign(exit.realizedPnL)}
      </td>
      <td className="py-2 px-3 text-sm text-gray-900">
        {exit.rMultiple && !isNaN(exit.rMultiple) ? `${exit.rMultiple.toFixed(2)}R` : 'N/A'}
      </td>
      <td className="py-2 px-3 text-sm text-gray-500">{exit.exitReason || '-'}</td>
    </tr>
  );
};

/**
 * Loading Skeleton
 */
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="animate-pulse">
        <div className="h-24 bg-gray-200 rounded-lg"></div>
      </div>
    ))}
  </div>
);

/**
 * Enriched Tranches Cards Component
 */
const EnrichedTranchesCards: React.FC<EnrichedTranchesCardsProps> = ({ 
  tranches, 
  isLoading = false,
  showHeader = true
}) => {
  if (isLoading) {
    return (
      <div className="mb-8">
        {showHeader && <h2 className="text-xl font-semibold text-gray-900 mb-4">Investment Tranches</h2>}
        <LoadingSkeleton />
      </div>
    );
  }

  if (tranches.length === 0) {
    return (
      <div className="mb-8">
        {showHeader && <h2 className="text-xl font-semibold text-gray-900 mb-4">Investment Tranches</h2>}
        <Card className="p-8 text-center">
          <p className="text-gray-500">No buy tranches yet. Add your first position!</p>
        </Card>
      </div>
    );
  }

  // Sort tranches by date in ascending order (oldest first)
  // T1 = earliest tranche, T2 = second tranche, etc.
  const sortedTranches = [...tranches].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <div className={showHeader ? "mb-8" : ""}>
      {showHeader && <h2 className="text-xl font-semibold text-gray-900 mb-4">Investment Tranches</h2>}
      
      {sortedTranches.map((tranche, index) => (
        <TrancheCard 
          key={tranche.trancheId} 
          tranche={tranche} 
          index={index}
        />
      ))}
    </div>
  );
};

export default EnrichedTranchesCards;
